import axios from "axios";
import { MaterialReactTable, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useEffect, useState } from "react";
import { UrlUpdateType } from "use-query-params";
import ReviewRemovalRequestModal from "../../RemovalRequest/ReviewRemovalRequestModal";
import { PersonRecord } from "../../types";
import { personRecordColumns } from "../PersonRecordColumns";
import PersonEntityHeader from "./PersonEntityHeader";

interface PersonRecordsTabProps {
  selectedEntityId?: number;
  setSelectedEntityId: (newValue: string | null | undefined, updateType?: UrlUpdateType) => void;
  onEdit?: (entityId: number) => void;
}

const PersonRecordsTab = ({ selectedEntityId, setSelectedEntityId, onEdit }: PersonRecordsTabProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [personRecords, setPersonRecords] = useState<PersonRecord[]>([]);
  const [selectedPersonWithRemovalRequest, setSelectedPersonWithRemovalRequest] = useState<PersonRecord | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const fetchPersonRecords = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.post("/api/blacklist/getPersonRecordsPaginated", {
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        searchTerm: globalFilter,
        sortField: sorting.length > 0 ? sorting[0].id : "",
        sortDescending: sorting.length > 0 ? sorting[0].desc : false
      });
      setPersonRecords(data.data);
      setRowCount(data.count);
    } catch (error) {
      console.error("Error fetching person records", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPersonWithRemovalRequest = async (personId: number) => {
    setIsModalLoading(true);
    try {
      const { data } = await axios.get<PersonRecord>(`/api/blacklist/GetPersonRecordById?id=${personId}`);
      if (data.removalRequest) {
        setSelectedPersonWithRemovalRequest(data);
      } else {
        setSelectedEntityId(undefined);
      }
    } catch (error) {
      console.error("Error fetching person records", error);
    } finally {
      setIsModalLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonRecords();
  }, [pagination.pageIndex, pagination.pageSize, sorting, globalFilter]);

  useEffect(() => {
    if (!selectedEntityId) return;
    fetchPersonWithRemovalRequest(selectedEntityId);
  }, [selectedEntityId]);

  const handleCloseModal = (): void => {
    setSelectedPersonWithRemovalRequest(null);
    setSelectedEntityId(undefined);
  };

  const handleRefreshUpdatedRequest = async (): Promise<void> => {
    await fetchPersonRecords();
  };

  return (
    <>
      <MaterialReactTable
        enableStickyHeader
        columns={personRecordColumns(fetchPersonRecords, onEdit)}
        data={personRecords}
        initialState={{ columnPinning: { left: ['edit'] } }}
        manualPagination
        manualFiltering
        manualSorting
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        onGlobalFilterChange={setGlobalFilter}
        rowCount={rowCount}
        state={{
          isLoading: isLoading,
          pagination,
          sorting,
          globalFilter
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: { backgroundColor: row.index % 2 === 0 ? "#f4f4f4" : "white" }
        })}
      />

      {selectedPersonWithRemovalRequest?.removalRequest && (
        <ReviewRemovalRequestModal
          open={Boolean(selectedPersonWithRemovalRequest)}
          handleClose={handleCloseModal}
          removalRequest={selectedPersonWithRemovalRequest.removalRequest}
          onActionComplete={handleRefreshUpdatedRequest}
          BlacklistDetailsComponent={<PersonEntityHeader person={selectedPersonWithRemovalRequest} />}
        />
      )}
    </>
  );
};

export default PersonRecordsTab;
